/* RescheduleRequestDialog — patient picks 1-3 alternative slots.
 *
 * The dialog runs entirely client-side: patient enters dates + times
 * (HTML5 date / time inputs, no slot grid for v1). On submit:
 *   POST /api/patient/appointments/{id}/reschedule-request
 *     body: { preferred_slots: [{date, time}], reason }
 *
 * Backend validates against doctor.business_hours + conflict check
 * **at approval time**, not now — patients can propose any time they
 * want, the clinic is the gatekeeper. This keeps the dialog simple
 * and lets the patient suggest slots outside posted hours (a friendly
 * "please squeeze me in at 7pm" gesture the clinic can accept or not).
 *
 * Validation we DO enforce here:
 *   - slots in the future (no asking to reschedule into yesterday)
 *   - 1-3 slots, no duplicates
 *   - basic "fill all fields of any added row" check
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, ArrowClockwise, Info } from "@phosphor-icons/react";
import TimeSlotSelect from "@/components/common/TimeSlotSelect";

const EMPTY_SLOT = { date: "", time: "" };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function RescheduleRequestDialog({ open, appt, onClose, onSuccess }) {
    const [slots, setSlots] = useState([{ ...EMPTY_SLOT }]);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const update = (idx, field, value) => {
        setSlots((arr) => arr.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
    };
    const addSlot = () => {
        if (slots.length >= 3) return;
        setSlots((arr) => [...arr, { ...EMPTY_SLOT }]);
    };
    const removeSlot = (idx) => {
        if (slots.length <= 1) return;
        setSlots((arr) => arr.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        // Trim empties
        const valid = slots.filter((s) => s.date && s.time);
        if (valid.length === 0) {
            toast.error("Add at least one alternative slot");
            return;
        }
        // Future-only check — backend doesn't enforce this (it leaves
        // calendar-policy to the clinic), but a "yesterday at 10am"
        // proposal makes no sense for the user.
        const now = Date.now();
        for (const s of valid) {
            const t = new Date(`${s.date}T${s.time}:00`).getTime();
            if (Number.isNaN(t) || t < now) {
                toast.error("All slots must be in the future");
                return;
            }
        }
        // Dedupe
        const seen = new Set();
        for (const s of valid) {
            const key = `${s.date}-${s.time}`;
            if (seen.has(key)) {
                toast.error("Slots can't be duplicates — pick distinct dates/times");
                return;
            }
            seen.add(key);
        }

        setBusy(true);
        try {
            await api.post(`/patient/appointments/${appt.id}/reschedule-request`, {
                preferred_slots: valid,
                reason: reason.trim(),
            });
            toast.success("Reschedule request sent — we'll notify you when the clinic decides");
            onSuccess?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send the request");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-lg" data-testid="reschedule-request-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-lg font-bold flex items-center gap-2">
                        <ArrowClockwise size={20} weight="duotone" className="text-primary" />
                        Request a reschedule
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Currently booked: <span className="font-medium text-foreground">{appt?.date} · {appt?.time}</span> with {appt?.doctor_name || "your doctor"}.
                    </p>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-lg bg-primary/5 border border-primary/15 px-3 py-2.5 flex items-start gap-2 text-xs">
                        <Info size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
                        <p className="text-foreground/85 leading-snug">
                            Suggest up to 3 alternative slots that work for you. The clinic will
                            confirm one or get back to you if none of these work.
                        </p>
                    </div>

                    <div className="space-y-2.5">
                        {slots.map((s, idx) => (
                            <div key={idx} className="flex items-end gap-2" data-testid={`slot-row-${idx}`}>
                                <div className="flex-1">
                                    <Label className="text-xs">Date</Label>
                                    <Input
                                        type="date"
                                        min={todayISO()}
                                        value={s.date}
                                        onChange={(e) => update(idx, "date", e.target.value)}
                                        className="h-10 mt-1"
                                        data-testid={`slot-date-${idx}`}
                                    />
                                </div>
                                <div className="flex-1">
                                    <Label className="text-xs">Time</Label>
                                    <div className="mt-1">
                                        <TimeSlotSelect
                                            value={s.time}
                                            onChange={(v) => update(idx, "time", v)}
                                            date={s.date}
                                            testId={`slot-time-${idx}`}
                                        />
                                    </div>
                                </div>
                                {slots.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeSlot(idx)}
                                        className="w-10 h-10 rounded-md hover:bg-rose-50 grid place-items-center text-muted-foreground hover:text-rose-600 shrink-0"
                                        title="Remove this slot"
                                        data-testid={`slot-remove-${idx}`}
                                    >
                                        <Trash size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {slots.length < 3 && (
                            <button
                                type="button"
                                onClick={addSlot}
                                className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="add-slot-btn"
                            >
                                <Plus size={12} weight="bold" /> Add another slot ({slots.length} of 3)
                            </button>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="reschedule-reason" className="text-xs">Why are you rescheduling? <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Textarea
                            id="reschedule-reason"
                            rows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Anything that helps the clinic understand — e.g. work conflict, feeling unwell"
                            className="mt-1 text-sm"
                            data-testid="reschedule-reason"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="reschedule-submit-btn">
                        {busy ? "Sending…" : "Send request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
